/**
 * 商品管理頁面
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { 
  Package, ChevronLeft, Plus, Edit, Archive, CheckCircle,
  AlertCircle, DollarSign, Boxes
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

export function ProductManagement() {
  const navigate = useNavigate();
  const { user, isAdmin, loading: authLoading } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [formData, setFormData] = useState({
    stock_quantity: 0,
    low_stock_threshold: 10,
  });

  useEffect(() => {
    if (!authLoading) {
      if (!user) {
        navigate('/login');
      } else if (!isAdmin()) {
        navigate('/member');
      }
    }
  }, [user, authLoading, isAdmin, navigate]);

  useEffect(() => {
    if (user && isAdmin()) {
      fetchProducts();
    }
  }, [user]);

  const fetchProducts = async () => {
    try {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setProducts(data || []);
    } catch (error) {
      console.error('查詢商品失敗:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStockUpdate = async () => {
    if (!selectedProduct) return;

    try {
      const { error } = await supabase
        .from('products')
        .update({
          stock_quantity: parseInt(formData.stock_quantity),
          low_stock_threshold: parseInt(formData.low_stock_threshold),
          updated_at: new Date().toISOString(),
        })
        .eq('id', selectedProduct.id);

      if (error) throw error;

      setDialogOpen(false);
      fetchProducts();
      alert('庫存更新成功！');
    } catch (error) {
      console.error('更新庫存失敗:', error);
      alert('更新失敗，請稍後再試');
    }
  };

  const openStockDialog = (product) => {
    setSelectedProduct(product);
    setFormData({
      stock_quantity: product.stock_quantity || 0,
      low_stock_threshold: product.low_stock_threshold || 10,
    });
    setDialogOpen(true);
  };

  if (authLoading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const totalProducts = products.length;
  const activeProducts = products.filter(p => p.is_active).length;
  const lowStockProducts = products.filter(p => 
    p.stock_quantity <= (p.low_stock_threshold || 10)
  ).length;
  const totalValue = products.reduce((sum, p) => 
    sum + (p.price * (p.stock_quantity || 0)), 0
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-zinc-50 py-8">
      <div className="container mx-auto px-4 max-w-7xl">
        {/* 返回按鈕 */}
        <Button
          variant="ghost"
          onClick={() => navigate('/admin')}
          className="mb-6"
        >
          <ChevronLeft className="h-4 w-4 mr-2" />
          返回後台管理
        </Button>

        {/* 頁面標題 */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">商品管理</h1>
            <p className="text-gray-600">管理商品庫存和資訊</p>
          </div>
        </div>

        {/* 統計卡片 */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white border-0 shadow-lg">
            <CardHeader className="pb-2">
              <CardDescription className="text-blue-100">總商品數</CardDescription>
              <CardTitle className="text-3xl">{totalProducts}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2 text-blue-100">
                <Package className="h-4 w-4" />
                <span className="text-sm">所有商品</span>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white border-0 shadow-lg">
            <CardHeader className="pb-2">
              <CardDescription className="text-green-100">上架中</CardDescription>
              <CardTitle className="text-3xl">{activeProducts}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2 text-green-100">
                <CheckCircle className="h-4 w-4" />
                <span className="text-sm">可銷售商品</span>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-red-500 to-red-600 text-white border-0 shadow-lg">
            <CardHeader className="pb-2">
              <CardDescription className="text-red-100">低庫存警告</CardDescription>
              <CardTitle className="text-3xl">{lowStockProducts}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2 text-red-100">
                <AlertCircle className="h-4 w-4" />
                <span className="text-sm">需要補貨</span>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white border-0 shadow-lg">
            <CardHeader className="pb-2">
              <CardDescription className="text-purple-100">總庫存價值</CardDescription>
              <CardTitle className="text-3xl">
                ${(totalValue / 1000).toFixed(0)}K
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2 text-purple-100">
                <DollarSign className="h-4 w-4" />
                <span className="text-sm">NT$ {totalValue.toLocaleString()}</span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* 商品列表 */}
        <Card className="bg-white/80 backdrop-blur-xl border-white/20 shadow-lg">
          <CardHeader>
            <CardTitle>商品列表</CardTitle>
            <CardDescription>管理商品庫存和狀態</CardDescription>
          </CardHeader>
          
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-12">
                <Package className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">尚無商品</p>
              </div>
            ) : (
              <div className="space-y-4">
                {products.map((product) => (
                  <div
                    key={product.id}
                    className="border rounded-lg p-4 hover:bg-gray-50/50 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-semibold text-lg">{product.name}</h3>
                          <Badge className={product.is_active 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-gray-100 text-gray-800'
                          }>
                            {product.is_active ? '上架中' : '已下架'}
                          </Badge>
                          {(product.stock_quantity || 0) <= (product.low_stock_threshold || 10) && (
                            <Badge className="bg-red-100 text-red-800">
                              <AlertCircle className="h-3 w-3 mr-1" />
                              低庫存
                            </Badge>
                          )}
                        </div>
                        
                        <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                          {product.description}
                        </p>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <span className="text-gray-500">SKU:</span>
                            <p className="font-medium font-mono">{product.sku}</p>
                          </div>
                          <div>
                            <span className="text-gray-500">價格:</span>
                            <p className="font-medium text-blue-600">
                              NT$ {product.price.toLocaleString()}
                            </p>
                          </div>
                          <div>
                            <span className="text-gray-500">庫存:</span>
                            <p className={`font-medium ${
                              (product.stock_quantity || 0) <= (product.low_stock_threshold || 10)
                                ? 'text-red-600'
                                : 'text-green-600'
                            }`}>
                              <Boxes className="h-4 w-4 inline mr-1" />
                              {product.stock_quantity || 0} 件
                            </p>
                          </div>
                          <div>
                            <span className="text-gray-500">低庫存警示:</span>
                            <p className="font-medium">{product.low_stock_threshold || 10} 件</p>
                          </div>
                        </div>
                      </div>

                      <div className="ml-4">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openStockDialog(product)}
                        >
                          <Edit className="h-4 w-4 mr-2" />
                          更新庫存
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* 庫存更新對話框 */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>更新商品庫存</DialogTitle>
              <DialogDescription>
                {selectedProduct?.name}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="stock">目前庫存數量</Label>
                <Input
                  id="stock"
                  type="number"
                  min="0"
                  value={formData.stock_quantity}
                  onChange={(e) => setFormData({
                    ...formData,
                    stock_quantity: e.target.value
                  })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="threshold">低庫存警示數量</Label>
                <Input
                  id="threshold"
                  type="number"
                  min="0"
                  value={formData.low_stock_threshold}
                  onChange={(e) => setFormData({
                    ...formData,
                    low_stock_threshold: e.target.value
                  })}
                />
                <p className="text-sm text-gray-500">
                  當庫存低於此數量時會顯示警告
                </p>
              </div>

              <div className="flex gap-4">
                <Button
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                  className="flex-1"
                >
                  取消
                </Button>
                <Button
                  onClick={handleStockUpdate}
                  className="flex-1"
                >
                  更新
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

export default ProductManagement;
